/**
 * Die Prüfpunkte des Gerätetests. Grundlage: Konzept „TFM – Konzept“,
 * Abschnitt Pilot-Kern, „Gerätetest vor Etappe 1“.
 */

export type Status = 'ok' | 'fehler' | 'nicht_unterstuetzt' | 'offen';

export type Geraeteklasse = 'handy' | 'pc';

export type Modus = 'app' | 'browser';

export const PRUEFPUNKT_KEYS = [
  'installation',
  'passkey-anlegen',
  'passkey-anmelden',
  'passkey-vorschlag',
  'passkey-feld',
  'kamera',
  'scanner',
  'taschenlampe',
  'kamera-erlaubnis',
  'qr-grossbuchstaben',
  'qr-vergleich',
  'bildschirm-an',
  'zurueck-dialog',
  'zurueck-beobachtung',
  'tastatur-leisten',
  'tastatur-beobachtung',
  'push',
  'push-neustart',
  'dauerhafter-speicher',
  'offline-daten',
  'offline-start',
  'foto-warteschlange',
  'trusted-types',
  'csp-bericht',
  'windows-hello',
  'bluetooth',
  'passkey-vom-handy',
  'gemeinsames-konto',
] as const;

export type PruefpunktKey = (typeof PRUEFPUNKT_KEYS)[number];

/** Die Ansicht, in der ein Prüfpunkt durchgeführt wird. */
export type Ansicht =
  | 'installation'
  | 'passkey'
  | 'anmeldefeld'
  | 'kamera'
  | 'qr'
  | 'bildschirm'
  | 'zurueck'
  | 'tastatur'
  | 'push'
  | 'speicher'
  | 'fotos'
  | 'sicherheit'
  | 'pc';

export interface Pruefpunkt {
  key: PruefpunktKey;
  titel: string;
  gruppe: string;
  ansicht: Ansicht;
  klassen: readonly Geraeteklasse[];
  /** Laut Konzept vor allem am iPhone zu prüfen. */
  iphone?: boolean;
  /** In App und Browser getrennt aussagekräftig; die Auswertung zeigt dann beide Ergebnisse. */
  beideModi?: boolean;
}

export const PRUEFPUNKTE: readonly Pruefpunkt[] = [
  { key: 'installation', titel: 'Als App installiert', gruppe: 'Installation', ansicht: 'installation', klassen: ['handy'] },
  { key: 'passkey-anlegen', titel: 'Passkey anlegen', gruppe: 'Anmeldung', ansicht: 'passkey', klassen: ['handy', 'pc'] },
  { key: 'passkey-anmelden', titel: 'Mit Passkey anmelden', gruppe: 'Anmeldung', ansicht: 'passkey', klassen: ['handy', 'pc'] },
  { key: 'passkey-vorschlag', titel: 'Passkey im Anmeldefeld angeboten', gruppe: 'Anmeldung', ansicht: 'anmeldefeld', klassen: ['handy', 'pc'], beideModi: true },
  { key: 'passkey-feld', titel: 'Anmeldung über das Anmeldefeld', gruppe: 'Anmeldung', ansicht: 'anmeldefeld', klassen: ['handy', 'pc'], beideModi: true },
  { key: 'kamera', titel: 'Kamera', gruppe: 'Kamera', ansicht: 'kamera', klassen: ['handy'] },
  { key: 'scanner', titel: 'Scanner in der App', gruppe: 'Kamera', ansicht: 'kamera', klassen: ['handy'] },
  { key: 'taschenlampe', titel: 'Taschenlampe', gruppe: 'Kamera', ansicht: 'kamera', klassen: ['handy'] },
  { key: 'kamera-erlaubnis', titel: 'Kamera-Erlaubnis bleibt', gruppe: 'Kamera', ansicht: 'kamera', klassen: ['handy'], iphone: true },
  { key: 'qr-grossbuchstaben', titel: 'QR-Link in Großbuchstaben', gruppe: 'QR-Aufkleber', ansicht: 'qr', klassen: ['handy'] },
  { key: 'qr-vergleich', titel: 'QR-Link klein geschrieben (Vergleich)', gruppe: 'QR-Aufkleber', ansicht: 'qr', klassen: ['handy'] },
  { key: 'bildschirm-an', titel: 'Bildschirm bleibt an', gruppe: 'Durchlauf', ansicht: 'bildschirm', klassen: ['handy'], iphone: true },
  { key: 'zurueck-dialog', titel: 'Zurück-Geste schließt zuerst den Dialog', gruppe: 'Bedienung', ansicht: 'zurueck', klassen: ['handy'], beideModi: true },
  { key: 'zurueck-beobachtung', titel: 'Zurück-Geste: Seite bleibt (beobachtet)', gruppe: 'Bedienung', ansicht: 'zurueck', klassen: ['handy'], beideModi: true },
  { key: 'tastatur-leisten', titel: 'Speichern-Knopf und Feld über der Tastatur', gruppe: 'Bedienung', ansicht: 'tastatur', klassen: ['handy'], beideModi: true },
  { key: 'tastatur-beobachtung', titel: 'Tastatur verdeckt nichts (beobachtet)', gruppe: 'Bedienung', ansicht: 'tastatur', klassen: ['handy'], beideModi: true },
  { key: 'push', titel: 'Push-Nachricht', gruppe: 'Push', ansicht: 'push', klassen: ['handy'] },
  { key: 'push-neustart', titel: 'Push nach Neustart', gruppe: 'Push', ansicht: 'push', klassen: ['handy'], iphone: true },
  { key: 'dauerhafter-speicher', titel: 'Dauerhafter Speicher', gruppe: 'Speicher und Offline', ansicht: 'speicher', klassen: ['handy'] },
  { key: 'offline-daten', titel: 'Offline-Daten nach Tagen', gruppe: 'Speicher und Offline', ansicht: 'speicher', klassen: ['handy'] },
  { key: 'offline-start', titel: 'Start ohne Netz', gruppe: 'Speicher und Offline', ansicht: 'speicher', klassen: ['handy'] },
  { key: 'foto-warteschlange', titel: 'Foto-Warteschlange nach Abbruch', gruppe: 'Speicher und Offline', ansicht: 'fotos', klassen: ['handy'] },
  { key: 'trusted-types', titel: 'Seite mit CSP und Trusted Types', gruppe: 'Sicherheit', ansicht: 'sicherheit', klassen: ['handy'], iphone: true, beideModi: true },
  { key: 'csp-bericht', titel: 'CSP-Verstoß an den Server gemeldet', gruppe: 'Sicherheit', ansicht: 'sicherheit', klassen: ['handy'], beideModi: true },
  { key: 'windows-hello', titel: 'Windows Hello', gruppe: 'Büro-PC', ansicht: 'pc', klassen: ['pc'] },
  { key: 'bluetooth', titel: 'Bluetooth', gruppe: 'Büro-PC', ansicht: 'pc', klassen: ['pc'] },
  { key: 'passkey-vom-handy', titel: 'Passkey vom Handy', gruppe: 'Büro-PC', ansicht: 'pc', klassen: ['pc'] },
  { key: 'gemeinsames-konto', titel: 'Gemeinsames Windows-Konto', gruppe: 'Büro-PC', ansicht: 'pc', klassen: ['pc'] },
];

export function pruefpunkt(key: PruefpunktKey): Pruefpunkt {
  const p = PRUEFPUNKTE.find((x) => x.key === key);
  if (!p) throw new Error(`Unbekannter Prüfpunkt: ${key}`);
  return p;
}

export function istPruefpunktKey(wert: unknown): wert is PruefpunktKey {
  return typeof wert === 'string' && (PRUEFPUNKT_KEYS as readonly string[]).includes(wert);
}

export const STATUS_WERTE: readonly Status[] = ['ok', 'fehler', 'nicht_unterstuetzt', 'offen'];

export const STATUS_TEXT: Record<Status, string> = {
  ok: 'Bestanden',
  fehler: 'Nicht bestanden',
  nicht_unterstuetzt: 'Nicht unterstützt',
  offen: 'Offen',
};

export const STATUS_SYMBOL: Record<Status, string> = {
  ok: '✓',
  fehler: '✗',
  nicht_unterstuetzt: '–',
  offen: '○',
};

/** Ab so vielen Tagen ohne Nutzung gilt die Prüfung der Offline-Daten als aussagekräftig. */
export const OFFLINE_MIN_PAUSE_TAGE = 3;
